import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";
import AboutUsStep from "./components/AboutUsStep";
import FAQ from "./components/FAQ";
import MailChimp from "./components/MailChimp";
import TestimonialCard from "./components/TestimonialCard";
import configs from "./configs/envs";

function App({ appName = "spoty" }: { appName?: keyof typeof configs }) {
  const {
    TITLE_TOP_HEADER,
    HERO_MAIN_TITLE,
    HERO_MAIN_SUBTITLE,
    HERO_MAIN_DESCRIPTION,
    HERO_MAIN_IMAGE,
    HERO_MAIN_MASCOT,
    STORES_LINKS,
    HOW_IT_WORKS_ITEMS,
    TESTIMONIALS,
    FAQS,
    FOOTER_APP_NAME,
    FOOTER_EMAIL_SUBSCRIPTION,
    FOOTER_EMAIL_SUBSCRIPTION_MAILCHIMP_U,
    FOOTER_EMAIL_SUBSCRIPTION_MAILCHIMP_ID,
    PRIMARY_COLOR,
    SHOW_FAQS,
  } = configs[appName];

  const CURRENT_YEAR = new Date().getFullYear();

  return (
    <div className="App">
      <div className={`${PRIMARY_COLOR} text-white py-10`}>
        {/* Header */}
        <div className="pb-4 max-w-screen-lg mx-auto text-center flex flex-row">
          <div className="inline-block mr-8 flex flex-1 justify-center">
            <a href="#how_it_works">
              <span className="font-bold">How it works?</span>
            </a>
          </div>
          <div className="inline-block w-auto flex flex-1 justify-center">
            <span className="text-2xl font-bold">{TITLE_TOP_HEADER}</span>
          </div>
          {SHOW_FAQS ? (
            <div className="inline-block ml-8 flex flex-1 justify-center">
              <a href="#faq">
                <span className="font-bold">FAQ</span>
              </a>
            </div>
          ) : (
            <div className="flex flex-1"></div>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className={`${PRIMARY_COLOR} text-white relative`}>
        <div className="max-w-screen-lg mx-auto text-center">
          <div className="flex items-center justify-center">
            <div className="flex flex-col justify-center w-1/2 text-left px-10">
              <p className="text-6xl lg:text-8xl font-bold">
                {HERO_MAIN_TITLE}
              </p>
              <p className="text-6xl lg:text-8xl font-bold">
                {HERO_MAIN_SUBTITLE}
              </p>
              <p className="py-10">{HERO_MAIN_DESCRIPTION}</p>
            </div>
            {HERO_MAIN_MASCOT && (
              <img
                alt="Mascot"
                className="object-contain z-10 relative w-1/3"
                src={HERO_MAIN_MASCOT}
              />
            )}
          </div>

          <img
            alt="Example of app being used on main screens"
            className="object-contain z-10 relative w-full lg:w-2/3 mx-auto"
            src={HERO_MAIN_IMAGE}
          />
        </div>
        <div className="bg-white w-full h-64 bottom-0 absolute z-0" />
      </div>

      {STORES_LINKS.length > 0 ? (
        <div className="flex items-center justify-center">
          {STORES_LINKS.map(({ url, type }, index) => {
            if (type === "android") {
              return (
                <GooglePlayButton
                  theme="dark"
                  key={`${index}-store-link`}
                  className="m-4"
                  url={url}
                />
              );
            }
            return (
              <AppStoreButton
                theme="dark"
                key={`${index}-store-link`}
                className="m-4"
                url={url}
              />
            );
          })}
        </div>
      ) : (
        <div className="max-w-screen-lg mx-auto">
          <img
            alt="Download Android and iOS - stores"
            className="w-96 mx-auto"
            src={require("./images/download button.png")}
          />
        </div>
      )}

      {/* About Us Section */}
      <div className="bg-white relative">
        <div id="how_it_works" className="max-w-screen-lg mx-auto">
          {HOW_IT_WORKS_ITEMS.map(({ TITLE, DESCRIPTION, IMAGE }, index) => (
            <AboutUsStep
              key={`${index}-about-us`}
              primary_color={PRIMARY_COLOR}
              second_color=""
              title={TITLE}
              message={DESCRIPTION}
              image={IMAGE}
              imageLeft={index % 2 === 1}
            />
          ))}
        </div>
      </div>
      {TESTIMONIALS.length > 0 && (
        <div className="bg-white relative">
          <div className="max-w-screen-lg mx-auto mt-20">
            <p className="text-6xl font-bold pb-10 text-center">Testimonials</p>
            <p className="max-w-xl mx-auto">
              A few friends and family already used the app and they have a
              great feedback to share with you.
            </p>

            <div className="flex flex-wrap items-center justify-around pt-10">
              {TESTIMONIALS.map(({ AUTHOR, COMMENT, PLACE }, index) => (
                <TestimonialCard
                  key={`${index}-testimonial-card`}
                  place={PLACE}
                  author={AUTHOR}
                  testimonial={COMMENT}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {SHOW_FAQS && (
        <div id="faq" className="max-w-screen-lg mx-auto">
          <p className="text-8xl font-bold pb-10 text-center">FAQ’s</p>

          <div className="max-w-3xl">
            <div className="flex flex-col text-left border-b-2 pb-5 mt-8">
              {FAQS.map(({ ANSWER, QUESTION }, index) => (
                <FAQ key={`${index}-faq`} question={QUESTION} answer={ANSWER} />
              ))}
            </div>
          </div>
        </div>
      )}

      {FOOTER_EMAIL_SUBSCRIPTION && (
        <div
          className={`${PRIMARY_COLOR} max-w-screen-lg mx-auto text-white p-10 rounded-md`}
        >
          <p className="text-left text-3xl font-bold mb-5">
            Subscribe to get updated
          </p>
          <p className="text-left max-w-2xl mb-5">
            Add your email to receive news about {FOOTER_APP_NAME}.
          </p>

          <div className="flex">
            <MailChimp
              user={FOOTER_EMAIL_SUBSCRIPTION_MAILCHIMP_U}
              id={FOOTER_EMAIL_SUBSCRIPTION_MAILCHIMP_ID}
            />
          </div>
        </div>
      )}

      <div className="bg-black text-white mt-32 h-24 flex">
        <span className="m-auto text-center">
          Copyright © {CURRENT_YEAR} all rights reserved {FOOTER_APP_NAME}.{" "}
          <br />
          <span className="font-bold">Made with love in 🇩🇪 & 🇵🇹</span>
        </span>
      </div>
    </div>
  );
}

export default App;
